import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Users, Plus, Spinner, Pencil, Trash, Wallet, House, Prohibit, CheckCircle, FileText,
  MagnifyingGlass, Funnel, X, Warning, Gauge, BatteryCharging,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const hasPerm = (key) => {
    if (!isStaff) return true;
    // payment_* izinleri icin acik true olmali
    if (key.startsWith("payment_")) return user?.permissions?.[key] === true;
    return user?.permissions?.[key] !== false;
  };
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerSettings, setCustomerSettings] = useState({ topup_min_amount: 10, topup_max_amount: 1000 });
  const [apartments, setApartments] = useState([]);
  const [unitsList, setUnitsList] = useState([]);
  const [selectedApartmentId, setSelectedApartmentId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [existingTenantWarning, setExistingTenantWarning] = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", password: "", phone: "",
    permissions: { view_reports: true, view_consumption: true },
  });
  const [balanceAmount, setBalanceAmount] = useState("");
  const [topupPreview, setTopupPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(() => {
    if (isStaff) {
      if (user?.permissions?.payment_cash !== false) return "cash";
      if (user?.permissions?.payment_bank !== false) return "bank";
    }
    return "cash";
  });

  const fetchTenants = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants`);
      setTenants(response.data);
    } catch { toast.error("Kiracilar yuklenirken hata olustu"); }
    finally { setLoading(false); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/settings`);
      setCustomerSettings(res.data);
    } catch { /* ignore for non-customer roles */ }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers`);
      setCustomers(res.data);
      if (res.data.length === 1) setSelectedCustomerId(res.data[0].id);
    } catch { /* ignore */ }
  }, []);

  const fetchApartments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/apartments`);
      setApartments(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchTenants();
    fetchSettings();
    fetchApartments();
    if (isAdmin) fetchCustomers();
  }, [fetchTenants, fetchSettings, fetchApartments, fetchCustomers, isAdmin]);

  const fetchUnitsForApartment = async (apartmentId) => {
    if (!apartmentId) { setUnitsList([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/apartments/${apartmentId}/units`);
      setUnitsList(res.data);
    } catch { setUnitsList([]); }
  };

  const handleApartmentChange = (aptId) => {
    setSelectedApartmentId(aptId);
    setSelectedUnitId("");
    setExistingTenantWarning(null);
    if (aptId) fetchUnitsForApartment(aptId);
    else setUnitsList([]);
  };

  const handleUnitChange = (unitId) => {
    setSelectedUnitId(unitId);
    if (!unitId) { setExistingTenantWarning(null); return; }
    const unit = unitsList.find(u => u.id === unitId);
    if (unit?.tenant_id && unit?.tenant_name) {
      setExistingTenantWarning({ tenantName: unit.tenant_name, unitName: unit.name });
    } else {
      setExistingTenantWarning(null);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (isAdmin && !selectedCustomerId) {
      toast.error("Lutfen bir musteri secin");
      return;
    }
    setFormLoading(true);
    try {
      const payload = { ...formData };
      if (selectedUnitId) payload.unit_ids = [selectedUnitId];
      const url = isAdmin
        ? `${API_URL}/api/tenants?customer_id=${selectedCustomerId}`
        : `${API_URL}/api/tenants`;
      await axios.post(url, payload);
      toast.success(selectedUnitId ? t("tenants.created_with_unit") : t("tenants.created"));
      setDialogOpen(false);
      setFormData({ name: "", email: "", password: "", phone: "", permissions: { view_reports: true, view_consumption: true } });
      setSelectedApartmentId("");
      setSelectedUnitId("");
      setExistingTenantWarning(null);
      setUnitsList([]);
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const openEditDialog = (tenant) => {
    setSelectedTenant({ ...tenant, editName: tenant.name, editEmail: tenant.email, editPhone: tenant.phone || "", editPassword: "" });
    setEditDialogOpen(true);
  };

  const openBalanceDialog = (tenant) => {
    setSelectedTenant(tenant);
    setBalanceAmount("");
    setTopupPreview(null);
    setBalanceDialogOpen(true);
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        name: selectedTenant.editName,
        email: selectedTenant.editEmail,
        phone: selectedTenant.editPhone,
      };
      if (selectedTenant.editPassword) payload.password = selectedTenant.editPassword;
      await axios.put(`${API_URL}/api/tenants/${selectedTenant.id}`, payload);
      toast.success("Kiraci guncellendi");
      setEditDialogOpen(false);
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const handleTopupPreview = async (amount) => {
    const tl = amount || parseFloat(balanceAmount);
    if (!tl || tl <= 0) { setTopupPreview(null); return; }
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tenants/${selectedTenant.id}/topup-preview?tl_amount=${tl}`);
      setTopupPreview(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(getErrorMessage(err));
      }
      setTopupPreview(null);
    } finally { setPreviewLoading(false); }
  };

  const handleTopupConfirm = async () => {
    if (!topupPreview || !selectedTenant) return;
    setFormLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/tenants/${selectedTenant.id}/add-balance?amount=${topupPreview.tl_amount}&payment_method=${paymentMethod}`);
      let msg = res.data.message || t("tenants.kwh_loaded");
      toast.success(msg);
      if (res.data.transaction_id) {
        setLastTxnId(res.data.transaction_id);
      }
      setBalanceDialogOpen(false);
      setTopupPreview(null);
      setBalanceAmount("");
      setPaymentMethod(isStaff && user?.permissions?.payment_cash === false ? "bank" : "cash");
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const [lastTxnId, setLastTxnId] = useState(null);

  const downloadReceipt = async (txnId) => {
    try {
      const res = await axios.get(`${API_URL}/api/transactions/${txnId}/receipt-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `makbuz_${txnId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Makbuz indirilemedi"); }
  };

  const handleDeleteTenant = async (tenantId) => {
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantId}`);
      toast.success("Kiraci silindi");
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setFormLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/tenants/bulk-delete`, selectedIds);
      toast.success(res.data.message);
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const handleToggleBlock = async (tenant) => {
    const newBlocked = !tenant.blocked;
    try {
      await axios.put(`${API_URL}/api/tenants/${tenant.id}/block?blocked=${newBlocked}`);
      toast.success(newBlocked ? `${tenant.name} engellendi` : `${tenant.name} erisimi acildi`);
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
  };

  const handleToggleFixedExpenseExempt = async (tenant) => {
    const newExempt = !tenant.fixed_expense_exempt;
    try {
      const res = await axios.put(`${API_URL}/api/tenants/${tenant.id}/fixed-expense-exempt?exempt=${newExempt}`);
      toast.success(res.data.message);
      fetchTenants();
    } catch (error) { toast.error(getErrorMessage(error)); }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredTenants.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredTenants.map(t => t.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Client-side search & filtering
  const filteredTenants = tenants.filter((t) => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const nameMatch = t.name?.toLowerCase().includes(q);
      const emailMatch = t.email?.toLowerCase().includes(q);
      const phoneMatch = t.phone?.toLowerCase().includes(q);
      const unitMatch = t.units?.some(u =>
        u.name?.toLowerCase().includes(q) || u.apartment_name?.toLowerCase().includes(q)
      );
      if (!nameMatch && !emailMatch && !phoneMatch && !unitMatch) return false;
    }
    // Status filter
    if (statusFilter === "debt") return (t.balance || 0) <= 0;
    if (statusFilter === "blocked") return t.blocked;
    if (statusFilter === "exempt") return t.fixed_expense_exempt;
    if (statusFilter === "active") return !t.blocked && (t.balance || 0) > 0;
    return true;
  });

  const debtCount = tenants.filter(t => (t.balance || 0) <= 0).length;
  const blockedCount = tenants.filter(t => t.blocked).length;
  const exemptCount = tenants.filter(t => t.fixed_expense_exempt).length;

  // Paginate
  const paginatedTenants = filteredTenants.slice((page - 1) * pageSize, page * pageSize);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("tenants.title")}</h1>
          <p className="text-slate-500 mt-1">{tenants.length} {t("tenants.registered")}</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.length > 0 && hasPerm("tenant_delete") && (
            <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="bulk-delete-btn">
                  <Trash size={16} className="mr-2" />
                  {selectedIds.length} {t("tenants.delete_selected")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("tenants.bulk_delete")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {selectedIds.length} {t("tenants.delete_selected")}?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700" disabled={formLoading}>
                    {formLoading ? t("common.loading") : `${selectedIds.length} ${t("tenants.delete_selected")}`}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {hasPerm("tenant_add") && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (open) { fetchApartments(); setSelectedApartmentId(""); setSelectedUnitId(""); setExistingTenantWarning(null); setUnitsList([]); }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-tenant-btn">
                <Plus size={18} className="mr-2" /> {t("tenants.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("tenants.add_title")}</DialogTitle>
                <DialogDescription>{t("tenants.add_desc")}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTenant}>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  {isAdmin && (
                    <div className="space-y-2">
                      <Label>{t("common.customer")}</Label>
                      <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                        <SelectTrigger data-testid="customer-select">
                          <SelectValue placeholder={t("common.select_customer")} />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("common.name")}</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="tenant-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("common.email")}</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="tenant-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t("common.password")}</Label>
                    <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="tenant-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("common.phone")}</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="tenant-phone-input" />
                  </div>
                  {/* Apartment & Unit Selection */}
                  <div className="space-y-4 pt-4 border-t border-slate-100">
                    <Label className="text-sm font-medium">{t("tenants.apartment_assign")}</Label>
                    <div className="space-y-2">
                      <Label htmlFor="apartment" className="text-sm text-slate-600">{t("common.apartment")}</Label>
                      <Select value={selectedApartmentId} onValueChange={handleApartmentChange}>
                        <SelectTrigger data-testid="tenant-apartment-select">
                          <SelectValue placeholder={t("tenants.select_apartment")} />
                        </SelectTrigger>
                        <SelectContent>
                          {apartments.map((a) => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedApartmentId && (
                      <div className="space-y-2">
                        <Label htmlFor="unit" className="text-sm text-slate-600">{t("common.unit")}</Label>
                        {unitsList.length === 0 ? (
                          <p className="text-sm text-slate-400">Bu apartmanda daire bulunamadi</p>
                        ) : (
                          <Select value={selectedUnitId} onValueChange={handleUnitChange}>
                            <SelectTrigger data-testid="tenant-unit-select">
                              <SelectValue placeholder={t("tenants.select_unit")} />
                            </SelectTrigger>
                            <SelectContent>
                              {unitsList.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name}{u.tenant_name ? ` (Mevcut: ${u.tenant_name})` : " (Bos)"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                    {existingTenantWarning && (
                      <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="existing-tenant-warning">
                        <Warning size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">{t("tenants.existing_tenant_warning")}</p>
                          <p className="text-amber-700">&quot;{existingTenantWarning.unitName}&quot; {t("tenants.existing_tenant_msg")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4 pt-4 border-t border-slate-100">
                    <Label className="text-sm font-medium">{t("tenants.permissions")}</Label>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-slate-600">{t("tenants.view_reports")}</Label>
                      <Switch checked={formData.permissions.view_reports}
                        onCheckedChange={(checked) => setFormData({ ...formData, permissions: { ...formData.permissions, view_reports: checked } })} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-slate-600">{t("tenants.view_consumption")}</Label>
                      <Switch checked={formData.permissions.view_consumption}
                        onCheckedChange={(checked) => setFormData({ ...formData, permissions: { ...formData.permissions, view_consumption: checked } })} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="tenant-submit-btn">
                    {formLoading ? t("common.creating") : t("common.create")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full sm:max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t("tenants.search_placeholder")}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-10 pr-9"
            data-testid="tenant-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="clear-search-btn">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Funnel size={16} className="text-slate-400" />
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="tenant-status-filter">
              <SelectValue placeholder={t("common.filter")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("tenants.filter_all")} ({tenants.length})</SelectItem>
              <SelectItem value="active">{t("common.active")}</SelectItem>
              <SelectItem value="debt">{t("tenants.filter_no_balance")} ({debtCount})</SelectItem>
              <SelectItem value="blocked">{t("tenants.filter_blocked")} ({blockedCount})</SelectItem>
              <SelectItem value="exempt">{t("tenants.filter_exempt")} ({exemptCount})</SelectItem>
            </SelectContent>
          </Select>
          {(searchQuery || statusFilter !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
              className="text-slate-500 hover:text-slate-700" data-testid="clear-filters-btn">
              <X size={14} className="mr-1" /> {t("common.clear")}
            </Button>
          )}
        </div>
      </div>

      {/* Results info */}
      {(searchQuery || statusFilter !== "all") && (
        <p className="text-sm text-slate-500" data-testid="filter-results-count">
          {filteredTenants.length} {t("common.results_found")}
          {searchQuery && <span> &middot; &quot;{searchQuery}&quot;</span>}
        </p>
      )}

      {/* Tenants Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {filteredTenants.length === 0 ? (
            <div className="py-16 text-center">
              <Users size={64} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {tenants.length === 0 ? t("tenants.no_tenants") : t("tenants.no_filter_match")}
              </h3>
              <p className="text-slate-500">
                {tenants.length === 0 ? t("tenants.no_tenants") : t("tenants.no_filter_match")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10">
                      <Checkbox checked={selectedIds.length === filteredTenants.length && filteredTenants.length > 0}
                        onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>{t("common.tenant")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("common.email")}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t("common.phone")}</TableHead>
                    <TableHead>{t("units.title")}</TableHead>
                    <TableHead>{t("common.balance")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("tenants.fixed_expense")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTenants.map((tenant) => (
                    <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}
                      className={`${tenant.blocked ? "bg-red-50/50" : ""} ${selectedIds.includes(tenant.id) ? "bg-indigo-50/50" : ""}`}>
                      <TableCell>
                        <Checkbox checked={selectedIds.includes(tenant.id)}
                          onCheckedChange={() => toggleSelect(tenant.id)}
                          data-testid={`select-tenant-${tenant.id}`} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{tenant.name}</span>
                          {tenant.blocked && (
                            <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid={`blocked-badge-${tenant.id}`}>
                              {t("tenants.blocked")}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{tenant.email}</TableCell>
                      <TableCell className="hidden lg:table-cell">{tenant.phone || "-"}</TableCell>
                      <TableCell>
                        {tenant.units?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {tenant.units.map((unit, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {unit.apartment_name} - {unit.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">{t("devices.unassigned")}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${tenant.balance > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {tenant.balance?.toFixed(2) || "0.00"} kWh
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {hasPerm("fixed_expense_exempt") ? (
                        <button
                          onClick={() => handleToggleFixedExpenseExempt(tenant)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer border ${
                            tenant.fixed_expense_exempt
                              ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          }`}
                          data-testid={`fixed-expense-toggle-${tenant.id}`}
                          title={tenant.fixed_expense_exempt ? "Sabit gider muafiyetini kaldir" : "Sabit giderlerden muaf tut"}
                        >
                          <span className={`w-2 h-2 rounded-full ${tenant.fixed_expense_exempt ? "bg-red-500" : "bg-emerald-500"}`} />
                          {tenant.fixed_expense_exempt ? t("tenants.exempt") : t("common.active")}
                        </button>
                        ) : (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                            tenant.fixed_expense_exempt
                              ? "bg-red-50 text-red-700 border-red-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}>
                          <span className={`w-2 h-2 rounded-full ${tenant.fixed_expense_exempt ? "bg-red-500" : "bg-emerald-500"}`} />
                          {tenant.fixed_expense_exempt ? t("tenants.exempt") : t("common.active")}
                        </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={tenant.blocked ? "bg-red-100 text-red-700" : tenant.balance > 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {tenant.blocked ? t("tenants.blocked") : tenant.balance > 0 ? t("common.active") : t("tenants.no_kwh")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {hasPerm("topup_load") && (
                          <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => openBalanceDialog(tenant)} data-testid={`add-balance-btn-${tenant.id}`} title={t("tenants.add_kwh")}>
                            <Wallet size={14} />
                          </Button>
                          )}
                          {hasPerm("tenant_edit") && (
                          <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => openEditDialog(tenant)} data-testid={`edit-tenant-btn-${tenant.id}`} title={t("common.edit")}>
                            <Pencil size={14} />
                          </Button>
                          )}
                          {hasPerm("tenant_remove") && (
                          <Button size="sm" variant={tenant.blocked ? "outline" : "outline"}
                            className={`h-8 px-2 ${tenant.blocked ? "text-emerald-600 hover:text-emerald-700" : "text-red-600 hover:text-red-700"}`}
                            onClick={() => handleToggleBlock(tenant)}
                            data-testid={`block-tenant-btn-${tenant.id}`}
                            title={tenant.blocked ? "Erisimi Ac" : "Erisimi Engelle"}>
                            {tenant.blocked ? <CheckCircle size={14} /> : <Prohibit size={14} />}
                          </Button>
                          )}
                          {hasPerm("tenant_delete") && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="h-8 px-2 text-red-600 hover:text-red-700" data-testid={`delete-tenant-btn-${tenant.id}`} title={t("common.delete")}>
                                <Trash size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("tenants.delete_title")}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {tenant.name} adli kiraciyi silmek istediginize emin misiniz?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteTenant(tenant.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {filteredTenants.length > 0 && (
        <Pagination total={filteredTenants.length} page={page} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={setPageSize} />
      )}

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("tenants.edit_title")}</DialogTitle>
            <DialogDescription>{selectedTenant?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateTenant}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Info banner */}
              {selectedTenant && (
                <div className="p-3 bg-slate-50 rounded-xl text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Kiraci ID:</span><span className="font-mono text-slate-700">{selectedTenant.id}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Kayit Tarihi:</span><span className="text-slate-700">{selectedTenant.created_at ? new Date(selectedTenant.created_at).toLocaleString("tr-TR") : "-"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">kWh:</span><span className="font-medium text-emerald-600">{(selectedTenant.balance || 0).toFixed(2)} kWh</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Musteri:</span><span className="text-slate-700">{selectedTenant.customer_id || "-"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t("common.status")}:</span><span className={selectedTenant.blocked ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>{selectedTenant.blocked ? t("tenants.blocked") : t("common.active")}</span></div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="edit-name">{t("common.name")}</Label>
                <Input id="edit-name" value={selectedTenant?.editName || ""}
                  onChange={(e) => setSelectedTenant({ ...selectedTenant, editName: e.target.value })}
                  required data-testid="edit-tenant-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">{t("common.email")}</Label>
                <Input id="edit-email" type="email" value={selectedTenant?.editEmail || ""}
                  onChange={(e) => setSelectedTenant({ ...selectedTenant, editEmail: e.target.value })}
                  required data-testid="edit-tenant-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">Yeni Sifre <span className="text-xs text-slate-400">(bos birakirsaniz degismez)</span></Label>
                <Input id="edit-password" type="password" value={selectedTenant?.editPassword || ""}
                  onChange={(e) => setSelectedTenant({ ...selectedTenant, editPassword: e.target.value })}
                  placeholder="Yeni sifre girin..." data-testid="edit-tenant-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">{t("common.phone")}</Label>
                <Input id="edit-phone" value={selectedTenant?.editPhone || ""}
                  onChange={(e) => setSelectedTenant({ ...selectedTenant, editPhone: e.target.value })}
                  data-testid="edit-tenant-phone" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="edit-tenant-submit">
                {formLoading ? t("common.updating") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* kWh Loading Dialog */}
      <Dialog open={balanceDialogOpen} onOpenChange={(open) => { setBalanceDialogOpen(open); if (!open) { setTopupPreview(null); setBalanceAmount(""); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTenant?.name} - {t("tenants.add_kwh")}</DialogTitle>
            <DialogDescription>
              TL → kWh {t("tenants.topup_desc")}
              {topupPreview && ` | ${t("common.tariff")}: ${topupPreview.tariff_name || "-"}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
            {/* Current kWh Balance */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
              <BatteryCharging size={20} className={`${(selectedTenant?.balance || 0) > 5 ? "text-emerald-500" : "text-red-500"}`} />
              <div className="flex-1">
                <p className="text-xs text-slate-500">{t("common.balance")}</p>
                <p className={`text-lg font-bold ${(selectedTenant?.balance || 0) > 5 ? "text-emerald-700" : "text-red-700"}`}>
                  {selectedTenant?.balance?.toFixed(2) || "0.00"} kWh
                </p>
              </div>
              {topupPreview?.device_name && (
                <div className="text-right">
                  <p className="text-xs text-slate-500">{t("common.meter")}</p>
                  <p className="text-sm font-medium text-slate-700">{topupPreview.device_name}</p>
                </div>
              )}
            </div>

            {/* Preset TL Buttons */}
            <div className="flex gap-2" data-testid="topup-presets">
              {[50, 100, 250, 500, 1000].map((val) => (
                <Button key={val} size="sm" variant="outline" className="flex-1 text-xs"
                  onClick={() => { setBalanceAmount(val.toString()); handleTopupPreview(val); }}
                  data-testid={`topup-preset-${val}`}>
                  {"\u20ba"}{val}
                </Button>
              ))}
            </div>

            {/* Payment Method Selection */}
            <div data-testid="payment-method-select">
              <p className="text-xs font-medium text-slate-600 mb-2">Ödeme Yöntemi</p>
              <div className="flex gap-2">
                {hasPerm("payment_cash") && (
                  <Button
                    size="sm"
                    variant={paymentMethod === "cash" ? "default" : "outline"}
                    className={`flex-1 ${paymentMethod === "cash" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                    onClick={() => setPaymentMethod("cash")}
                    data-testid="payment-method-cash"
                  >
                    Nakit
                  </Button>
                )}
                {hasPerm("payment_bank") && (
                  <Button
                    size="sm"
                    variant={paymentMethod === "bank" ? "default" : "outline"}
                    className={`flex-1 ${paymentMethod === "bank" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                    onClick={() => setPaymentMethod("bank")}
                    data-testid="payment-method-bank"
                  >
                    Havale/EFT
                  </Button>
                )}
              </div>
            </div>

            {/* Custom TL Amount */}
            <div className="flex gap-2">
              <Input type="number" step="0.01"
                min={customerSettings.topup_min_amount} max={customerSettings.topup_max_amount}
                placeholder={`TL (${customerSettings.topup_min_amount} - ${customerSettings.topup_max_amount})`}
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                className="flex-1"
                data-testid="balance-amount-input" />
              <Button variant="outline" onClick={() => handleTopupPreview()} disabled={previewLoading || !balanceAmount}
                data-testid="topup-preview-btn">
                {previewLoading ? <Spinner size={14} className="animate-spin" /> : t("topup.calculate") || "Hesapla"}
              </Button>
            </div>
            <p className="text-xs text-slate-400">Min: {customerSettings.topup_min_amount} TL - Max: {customerSettings.topup_max_amount} TL</p>

            {/* Preview Breakdown */}
            {topupPreview && (
              <div className="space-y-3">
                {/* Debt Warning */}
                {topupPreview.debt_amount > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="tenant-debt-warning">
                    <p className="text-xs font-medium text-red-700">{t("topup.unpaid_debt") || "Odenmemis Borc"}: {"\u20ba"}{topupPreview.debt_amount.toFixed(2)}</p>
                    <p className="text-xs text-red-600">
                      {t("topup.debt_deduct_info") || "Bu odemeden"} {"\u20ba"}{topupPreview.debt_deducted.toFixed(2)} {t("topup.debt_for") || "borc icin kesilecek"}, {t("topup.remaining_for_kwh") || "kalan"} {"\u20ba"}{topupPreview.effective_tl.toFixed(2)} kWh {t("topup.will_be_used") || "icin kullanilacak"}
                    </p>
                  </div>
                )}

                {/* Tier Warning */}
                {topupPreview.tier_warning && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="tenant-tier-warning">
                    <p className="text-xs font-medium text-amber-700">{topupPreview.tier_warning}</p>
                    <p className="text-xs text-amber-600">{t("topup.current") || "Mevcut"}: {t("topup.tier") || "Kademe"} {topupPreview.current_tier} | {t("topup.after") || "Sonrasi"}: {t("topup.tier") || "Kademe"} {topupPreview.after_tier}</p>
                  </div>
                )}

                {/* Breakdown Table */}
                {topupPreview.breakdown && topupPreview.breakdown.length > 0 && (
                  <div className="border border-indigo-200 rounded-xl overflow-hidden" data-testid="tenant-topup-breakdown">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-indigo-100/50">
                          <th className="text-left p-2 font-medium text-indigo-700">{t("topup.tier") || "Kademe"}</th>
                          <th className="text-left p-2 font-medium text-indigo-700">{t("topup.range") || "Aralik"}</th>
                          <th className="text-right p-2 font-medium text-indigo-700">{t("topup.unit_price") || "Birim Fiyat"}</th>
                          <th className="text-right p-2 font-medium text-indigo-700">kWh</th>
                          <th className="text-right p-2 font-medium text-indigo-700">{t("common.amount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topupPreview.breakdown.map((b, i) => (
                          <tr key={i} className="border-t border-indigo-100">
                            <td className="p-2 text-slate-700">{b.tier}. {t("topup.tier") || "Kademe"}</td>
                            <td className="p-2 text-slate-600">{b.range}</td>
                            <td className="p-2 text-right text-slate-700">{"\u20ba"}{b.price_per_kwh}</td>
                            <td className="p-2 text-right font-medium text-slate-900">{b.kwh}</td>
                            <td className="p-2 text-right font-medium text-slate-900">{"\u20ba"}{b.cost}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-indigo-300 bg-indigo-50">
                          <td colSpan={3} className="p-2 font-semibold text-indigo-700">{t("common.total")}</td>
                          <td className="p-2 text-right font-bold text-indigo-900">{topupPreview.total_kwh} kWh</td>
                          <td className="p-2 text-right font-bold text-indigo-900">{"\u20ba"}{topupPreview.total_cost}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* All money goes to debt */}
                {topupPreview.total_kwh === 0 && topupPreview.debt_deducted > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                    <p className="text-sm font-medium text-amber-700">{t("topup.all_to_debt") || "Tum tutar borca aktarilacak"}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Confirm / Cancel Buttons */}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setBalanceDialogOpen(false); setTopupPreview(null); setBalanceAmount(""); }}>{t("common.cancel")}</Button>
            {topupPreview ? (
              <Button onClick={handleTopupConfirm} disabled={formLoading}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="topup-confirm-btn">
                {formLoading ? <Spinner size={16} className="animate-spin mr-2" /> : <Gauge size={16} className="mr-2" />}
                {topupPreview.total_kwh > 0
                  ? `${topupPreview.total_kwh} kWh ${t("topup.load") || "Yukle"} (${"\u20ba"}${topupPreview.total_cost})`
                  : `${"\u20ba"}${topupPreview.debt_deducted?.toFixed(2)} ${t("topup.pay_debt") || "Borc Ode"}`
                }
                {topupPreview.debt_deducted > 0 && topupPreview.total_kwh > 0 ? ` + ${"\u20ba"}${topupPreview.debt_deducted.toFixed(2)} ${t("topup.debt_short") || "borc"}` : ""}
              </Button>
            ) : (
              <Button disabled className="bg-slate-300" data-testid="balance-submit-btn">
                <Gauge size={16} className="mr-2" /> {t("topup.calculate_first") || "Once Hesaplayin"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Download Toast - shown after successful topup */}
      {lastTxnId && (
        <div className="fixed bottom-6 right-6 z-50 bg-white border border-emerald-200 shadow-lg rounded-xl p-4 flex items-center gap-3" data-testid="receipt-toast">
          <FileText size={24} className="text-emerald-600" />
          <div>
            <p className="text-sm font-medium text-slate-900">{t("topup.success")}</p>
            <button onClick={() => { downloadReceipt(lastTxnId); setLastTxnId(null); }}
              className="text-sm text-indigo-600 hover:text-indigo-700 underline" data-testid="download-receipt-btn">
              {t("dashboard.receipt_download")}
            </button>
          </div>
          <button onClick={() => setLastTxnId(null)} className="text-slate-400 hover:text-slate-600 ml-2">
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
